import { when } from '@execonline-inc/maybe-adapter';
import { Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { emptyFragment } from '../../../../../../../EmptyFragment';
import { T } from '../../../../../../../Translations';
import Pagination from '../../../../../../Pagination';
import { hasResults } from '../../../../../../Pagination/pagination';
import { LearningDesignsResource } from '../../../../../Types';
import LearningCollectionEditStore from '../../../Store';
import { BrowseCatalog } from '../../../../Types';
import AddSectionButton from './AddSectionButton';
import Browse from './Browse';
import CompetencyFilter from './CompetencyFilter';
import CompetencyFilterStore from './CompetencyFilter/Store';
import { allCompetencyOptions } from './CompetencyFilter/Types';
import LearningDesign from './LearningDesign';
import ProductTypeFilter from './ProductTypeFilter';
import ProductTypeFilterStore from './ProductTypeFilter/Store';
import { allProductTypeOptions } from './ProductTypeFilter/Types';

interface Props {
  store: LearningCollectionEditStore;
  browseCatalog: BrowseCatalog;
  learningDesignsResource: LearningDesignsResource;
}

function FiltersWithLearningCollectionsReady({
  store,
  browseCatalog,
  learningDesignsResource,
}: Props) {
  const competencyFilterStore = useRef(
    new CompetencyFilterStore(learningDesignsResource.payload.competencyFilterOptions),
  ).current;
  const productTypeFilterStore = useRef(
    new ProductTypeFilterStore(learningDesignsResource.payload.productTypeFilterOptions),
  ).current;
  const [learningDesignId, setLearningDesignId] = useState<Maybe<number>>(nothing());

  return (
    <div className="mx-auto w-full max-w-clientPortalMaxWidth">
      <div className="px-2.5 pb-4 md:flex-row lg:px-12">
        <div className="pt-10">
          <h2 className="text-3xl font-semibold text-gray-900">
            <T kind="Select a curated section" />
          </h2>
          <div className="flex items-center space-x-5">
            <div className="pb-4 pt-1">
              <T kind="Choose one of the curated collections below, or create..." />
            </div>
            <Browse browseCatalog={browseCatalog} store={store} />
          </div>
          <div className="flex py-4">
            <CompetencyFilter
              competencyFilterStore={competencyFilterStore}
              options={allCompetencyOptions(
                learningDesignsResource.payload.competencyFilterOptions,
              )}
            />
            <ProductTypeFilter
              productTypeFilterStore={productTypeFilterStore}
              options={allProductTypeOptions(
                learningDesignsResource.payload.productTypeFilterOptions,
              )}
            />
          </div>
        </div>
        <div>
          {learningDesignsResource.payload.learningDesignResources.map((learningDesignResource) => (
            <LearningDesign
              competencyFilterStore={competencyFilterStore}
              productTypeFilterStore={productTypeFilterStore}
              learningDesignResource={learningDesignResource}
              key={learningDesignResource.payload.id}
              learningDesignId={learningDesignId}
              setLearningDesignId={setLearningDesignId}
            />
          ))}
        </div>
      </div>
      {when(hasResults(learningDesignsResource.metadata), true)
        .map(() => (
          <div className="px-2.5 py-5 lg:px-12">
            <Pagination
              metadata={learningDesignsResource.metadata}
              pageFn={store.learningDesignsStore.pageTo}
            />
          </div>
        ))
        .getOrElse(emptyFragment)}
      <AddSectionButton
        store={store}
        learningDesignsResource={learningDesignsResource}
        learningDesignId={learningDesignId}
      />
    </div>
  );
}

export default observer(FiltersWithLearningCollectionsReady);
